/**
 * Предустановка: Проект с конфликтом слияния
 * Используется для уроков по разрешению конфликтов
 */

import type { GitPreset } from './types';

/** Проект с конфликтом слияния для демонстрации разрешения конфликтов */
export const mergeConflictPreset: GitPreset = {
  id: 'merge-conflict',
  name: 'Проект с конфликтом слияния',
  description:
    'Проект с двумя ветками, изменяющими один и тот же файл, для изучения разрешения конфликтов',

  currentBranch: 'main',

  branches: [
    { name: 'main', head: 'c3', color: '#F05032' },
    { name: 'feature/update-config', head: 'c5', color: '#4a9eff' },
  ],

  commits: [
    {
      sha: 'c1',
      message: 'Initial commit: Add config and README',
      parents: [],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704067200, // 2024-01-01
      files: ['README.md', 'config.json'],
    },
    {
      sha: 'c2',
      message: 'Add application entry point',
      parents: ['c1'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704153600, // 2024-01-02
      files: ['src/index.ts', 'src/app.ts'],
    },
    {
      sha: 'c3',
      message: 'Update config: change API URL and add debug mode',
      parents: ['c2'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704240000, // 2024-01-03
      files: ['config.json'],
    },
    // Feature branch commits (изменяют тот же config.json)
    {
      sha: 'c4',
      message: 'Start feature: add new settings',
      parents: ['c2'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704240000, // 2024-01-03 (параллельно с c3)
      files: ['config.json', 'src/settings.ts'],
    },
    {
      sha: 'c5',
      message: 'Complete feature: update config format',
      parents: ['c4'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704326400, // 2024-01-04
      files: ['config.json'],
    },
  ],

  files: [
    {
      path: 'README.md',
      content: `# Configuration Demo Project

Демонстрационный проект для изучения конфликтов слияния.

## Структура

- \`config.json\` - конфигурация приложения
- \`src/index.ts\` - точка входа
- \`src/app.ts\` - основная логика
- \`src/settings.ts\` - управление настройками

## Использование

\`\`\`bash
npm start
\`\`\`
`,
      status: 'committed',
    },
    {
      path: 'config.json',
      // Это версия из main (c3) - будет конфликт с feature веткой
      content: `{
  "apiUrl": "https://api.production.example.com",
  "timeout": 5000,
  "debug": true,
  "version": "1.0.0"
}
`,
      status: 'committed',
    },
    {
      path: 'src/index.ts',
      content: `import { createApp } from './app';
import config from '../config.json';

const app = createApp(config);

app.start().then(() => {
  console.log('Application started');
}).catch((error) => {
  console.error('Failed to start:', error);
});
`,
      status: 'committed',
    },
    {
      path: 'src/app.ts',
      content: `import type { Config } from './types';

export function createApp(config: Config) {
  return {
    config,

    async start() {
      console.log('Starting app with config:', this.config);
      // Инициализация приложения
      await this.initialize();
    },

    async initialize() {
      // Симуляция инициализации
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('App initialized');
    },

    async stop() {
      console.log('Stopping app');
    }
  };
}
`,
      status: 'committed',
    },
    {
      path: 'src/types.ts',
      content: `export interface Config {
  apiUrl: string;
  timeout: number;
  debug: boolean;
  version: string;
}

export interface App {
  config: Config;
  start(): Promise<void>;
  initialize(): Promise<void>;
  stop(): Promise<void>;
}
`,
      status: 'committed',
    },
    {
      path: 'src/settings.ts',
      content: `import type { Config } from './types';

export class SettingsManager {
  private config: Config;

  constructor(config: Config) {
    this.config = config;
  }

  get<K extends keyof Config>(key: K): Config[K] {
    return this.config[key];
  }

  set<K extends keyof Config>(key: K, value: Config[K]): void {
    this.config[key] = value;
  }

  export(): Config {
    return { ...this.config };
  }
}
`,
      status: 'committed',
    },
  ],

  // Файлы с конфликтами (для демонстрации)
  workingDir: [
    {
      path: 'config.json',
      status: 'modified',
    },
  ],

  visualization: {
    branchColors: {
      main: '#F05032',
      'feature/update-config': '#4a9eff',
    },
    nodePositions: {
      c1: { x: 100, y: 200 },
      c2: { x: 200, y: 200 },
      c3: { x: 300, y: 200 },
      c4: { x: 300, y: 300 },
      c5: { x: 400, y: 300 },
    },
    annotations: [
      { sha: 'c3', text: 'Изменён config.json', position: 'top' },
      { sha: 'c5', text: 'Тоже изменён config.json - КОНФЛИКТ!', position: 'right' },
    ],
    highlightedCommits: ['c3', 'c5'],
  },
};

/**
 * Альтернативная версия config.json из feature ветки
 * Используется для демонстрации различий при конфликте
 */
export const featureConfigContent = `{
  "apiUrl": "https://api.staging.example.com",
  "timeout": 3000,
  "debug": false,
  "version": "1.1.0-beta",
  "features": {
    "newSettings": true,
    "experimentalUI": false
  }
}
`;

/**
 * Разрешённая версия config.json после слияния
 */
export const resolvedConfigContent = `{
  "apiUrl": "https://api.production.example.com",
  "timeout": 5000,
  "debug": true,
  "version": "1.1.0",
  "features": {
    "newSettings": true,
    "experimentalUI": false
  }
}
`;
